import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Sparkles, Ruler, Target, ArrowRight, RotateCcw,
  Mail, CheckCircle2, TrendingUp, Flame,
} from 'lucide-react';
import { toast } from 'sonner';
import { calculateAesthetics, saveAestheticsResults } from '../services/api';
import { SaveCalculatorButton } from './SaveCalculatorButton';
import MacroPhase from './MacroPhase';

const initialForm = {
  gender: 'male',
  age_years: '',
  height_cm: '',
  weight_kg: '',
  shoulder_cm: '',
  waist_cm: '',
  hip_cm: '',
};

const AestheticsCalculatorSection = ({ onBookClick, defaultGender }) => {
  const [form, setForm] = useState(() => ({ ...initialForm, gender: defaultGender || initialForm.gender }));
  const [result, setResult] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [saveEmail, setSaveEmail] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  // ---- Phase 2: Macronutrient calculator state ----
  const [macroOpen, setMacroOpen] = useState(false);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const buildPayload = () => {
    const payload = {
      gender: form.gender,
      height_cm: parseFloat(form.height_cm),
      waist_cm: parseFloat(form.waist_cm),
    };
    if (form.gender === 'male') {
      payload.shoulder_cm = parseFloat(form.shoulder_cm);
    } else {
      payload.hip_cm = parseFloat(form.hip_cm);
    }
    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsCalculating(true);
    try {
      const payload = buildPayload();
      const res = await calculateAesthetics(payload);
      setResult(res);
      setIsSaved(false);
      setTimeout(() => {
        const el = document.getElementById('aesthetics-results');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Számítási hiba történt.';
      toast.error(typeof detail === 'string' ? detail : 'Ellenőrizd a megadott adatokat.');
    } finally {
      setIsCalculating(false);
    }
  };

  const handleSaveResults = async (e) => {
    e.preventDefault();
    if (!saveEmail || !result) return;
    setIsSaving(true);
    try {
      await saveAestheticsResults({
        email: saveEmail,
        snapshot: buildPayload(),
        result,
      });
      setIsSaved(true);
      // Stash email for the Phase 2 macro auto-email trigger (source='auto').
      // Silent — key is namespaced and only used by MacroPhase.
      try {
        sessionStorage.setItem('oc_calc_email', saveEmail);
      } catch { /* sessionStorage unavailable */ }
      toast.success('Eredmények elmentve! Hamarosan megérkezik az e-mail.');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Mentési hiba történt.';
      toast.error(typeof detail === 'string' ? detail : 'Próbáld újra később.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setForm(initialForm);
    setResult(null);
    setSaveEmail('');
    setIsSaved(false);
    setMacroOpen(false);
  };

  // ---- Phase 2: Macros handlers ----
  const handleOpenMacroPhase = () => {
    setMacroOpen(true);
    setTimeout(() => {
      const el = document.getElementById('aesthetics-macro-phase');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  };

  // ratio bar position (0..100) — clamped against the visualization range
  const ratioBarPct = result ? Math.min(100, Math.max(0, (result.ratio / (result.ideal_ratio * 1.5)) * 100)) : 0;
  const idealBarPct = result ? Math.min(100, Math.max(0, (result.ideal_ratio / (result.ideal_ratio * 1.5)) * 100)) : 0;

  const cmFieldSuffix = {
    shoulder: 'vállszélességen',
    waist: 'deréken',
    hip: 'csípőn',
  };

  return (
    <section
      id="aesthetics-calculator"
      data-testid="aesthetics-calculator-section"
      className="py-24 bg-gradient-to-b from-gray-950 to-gray-900 relative overflow-hidden border-t border-gray-800"
    >
      {/* Subtle grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '32px 32px',
        }}
      ></div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 bg-yellow-600/20 border border-yellow-600/30 rounded-full">
            <Sparkles className="text-yellow-400" size={16} />
            <span className="text-yellow-400 font-semibold text-sm tracking-wider">
              ADONIS & VENUS INDEX
            </span>
          </div>

          <h2
            data-testid="aesthetics-heading"
            className="text-4xl md:text-5xl font-bold text-white mb-5"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            A klasszikus
            <span className="text-yellow-400"> Aranyarány</span>
          </h2>

          <p className="text-lg text-gray-300 leading-relaxed">
            A szobrászati ideál — Férfiaknak <strong className="text-yellow-400">1.618</strong> vállé-derék arány,
            Nőknek <strong className="text-yellow-400">0.7</strong> derék-csípő arány. Tudd meg, hol állsz hozzá képest.
          </p>
        </div>

        {/* Form card */}
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl p-5 sm:p-8 md:p-10">
          <div className="flex justify-end mb-3">
            <a
              href="/tudastar/gorog-szobrok-titka-adonis-venusz-index"
              className="text-xs text-yellow-700 hover:text-yellow-800 font-semibold inline-flex items-center gap-1"
              data-testid="aesthetics-guide-link"
            >
              <span>📏</span> Hogyan mérj centivel? — Útmutató
            </a>
          </div>
          <form onSubmit={handleSubmit} data-testid="aesthetics-form" className="space-y-6">
            {/* Gender toggle */}
            <div>
              <Label className="text-gray-700 font-semibold mb-2 block">Nem</Label>
              <div data-testid="aesthetics-gender-toggle" className="flex gap-1 p-1 bg-gray-100 rounded-lg max-w-md">
                {[
                  { v: 'male', l: 'Férfi (Adonis)' },
                  { v: 'female', l: 'Nő (Venus)' },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    data-testid={`aesthetics-gender-${opt.v}`}
                    onClick={() => handleChange('gender', opt.v)}
                    className={`flex-1 px-4 py-2.5 text-sm font-semibold rounded-md transition-all ${
                      form.gender === opt.v
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>
            </div>

            {/* Height + Waist always */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="min-w-0">
                <Label htmlFor="aest-height" className="text-gray-700 font-semibold mb-2 block">
                  Magasság (cm)
                </Label>
                <Input
                  data-testid="aesthetics-height-input"
                  id="aest-height"
                  type="number"
                  step="0.1"
                  min="100"
                  max="250"
                  required
                  value={form.height_cm}
                  onChange={(e) => handleChange('height_cm', e.target.value)}
                  placeholder="180"
                  className="h-11"
                />
              </div>
              <div className="min-w-0">
                <Label htmlFor="aest-waist" className="text-gray-700 font-semibold mb-2 block">
                  Derék kerülete (cm)
                </Label>
                <Input
                  data-testid="aesthetics-waist-input"
                  id="aest-waist"
                  type="number"
                  step="0.1"
                  min="40"
                  max="200"
                  required
                  value={form.waist_cm}
                  onChange={(e) => handleChange('waist_cm', e.target.value)}
                  placeholder="78"
                  className="h-11"
                />
              </div>
            </div>

            {/* Age + Weight (used by Phase 2 macro calc) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="min-w-0">
                <Label htmlFor="aest-age" className="text-gray-700 font-semibold mb-2 block">
                  Életkor (év)
                </Label>
                <Input
                  data-testid="aesthetics-age-input"
                  id="aest-age"
                  type="number"
                  min="14"
                  max="100"
                  required
                  value={form.age_years}
                  onChange={(e) => handleChange('age_years', e.target.value)}
                  placeholder="30"
                  className="h-11"
                />
              </div>
              <div className="min-w-0">
                <Label htmlFor="aest-weight" className="text-gray-700 font-semibold mb-2 block">
                  Testsúly (kg)
                </Label>
                <Input
                  data-testid="aesthetics-weight-input"
                  id="aest-weight"
                  type="number"
                  step="0.1"
                  min="30"
                  max="250"
                  required
                  value={form.weight_kg}
                  onChange={(e) => handleChange('weight_kg', e.target.value)}
                  placeholder="78"
                  className="h-11"
                />
              </div>
            </div>

            {/* Conditional shoulder (male) or hip (female) */}
            {form.gender === 'male' ? (
              <div>
                <Label htmlFor="aest-shoulder" className="text-gray-700 font-semibold mb-2 block">
                  Vállkörméret (cm)
                  <span className="text-xs font-normal text-gray-500 ml-2">
                    — a legszélesebb ponton, könyökkel a test mellett
                  </span>
                </Label>
                <Input
                  data-testid="aesthetics-shoulder-input"
                  id="aest-shoulder"
                  type="number"
                  step="0.1"
                  min="60"
                  max="200"
                  required
                  value={form.shoulder_cm}
                  onChange={(e) => handleChange('shoulder_cm', e.target.value)}
                  placeholder="120"
                  className="h-11"
                />
              </div>
            ) : (
              <div>
                <Label htmlFor="aest-hip" className="text-gray-700 font-semibold mb-2 block">
                  Csípő kerülete (cm)
                  <span className="text-xs font-normal text-gray-500 ml-2">
                    — a legszélesebb ponton
                  </span>
                </Label>
                <Input
                  data-testid="aesthetics-hip-input"
                  id="aest-hip"
                  type="number"
                  step="0.1"
                  min="40"
                  max="200"
                  required
                  value={form.hip_cm}
                  onChange={(e) => handleChange('hip_cm', e.target.value)}
                  placeholder="96"
                  className="h-11"
                />
              </div>
            )}

            <div className="pt-2">
              <Button
                data-testid="aesthetics-submit-btn"
                type="submit"
                disabled={isCalculating}
                className="w-full bg-yellow-600 hover:bg-yellow-700 text-white font-bold text-lg py-6 transition-all duration-300"
              >
                {isCalculating ? 'Számolás…' : 'Arányok kiszámítása'}
              </Button>
              <p className="text-xs text-gray-500 text-center mt-3">
                Klasszikus Adonis (1.618) & Venus (0.7) Index · Csak tájékoztató jellegű elemzés.
              </p>
            </div>
          </form>

          {/* Results */}
          {result && (
            <div
              id="aesthetics-results"
              data-testid="aesthetics-results"
              className="mt-10 pt-10 border-t border-gray-200"
            >
              <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <h3
                  className="text-2xl font-bold text-gray-900"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Az eredményeid
                </h3>
                <div className="flex items-center gap-3 flex-wrap">
                  <SaveCalculatorButton
                    type="adonis"
                    input={{
                      gender: form.gender, height_cm: form.height_cm, waist_cm: form.waist_cm,
                      shoulder_cm: form.shoulder_cm, hip_cm: form.hip_cm,
                    }}
                    output={{
                      score: result.score, ratio: result.ratio, ideal_ratio: result.ideal_ratio,
                      category: result.category, index_type: result.index_type,
                    }}
                    testid="calc-save-adonis"
                  />
                  <button
                    type="button"
                    data-testid="aesthetics-reset-btn"
                    onClick={handleReset}
                    className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors"
                  >
                    <RotateCcw size={14} /> Új számítás
                  </button>
                </div>
              </div>

              {/* Hero score card */}
              <div
                data-testid="aesthetics-score-card"
                className="bg-gradient-to-br from-gray-900 to-gray-800 text-white p-6 md:p-8 rounded-2xl border border-gray-800 mb-6"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                  <div>
                    <div className="flex items-center gap-2 text-yellow-400 text-xs font-bold tracking-widest uppercase mb-2">
                      <Sparkles size={14} />
                      {result.index_type === 'adonis' ? 'Adonis Index' : 'Venus Index'} · Pontszámod
                    </div>
                    <div
                      className="text-5xl md:text-6xl font-bold leading-none"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                      {result.score}
                      <span className="text-2xl text-yellow-400 ml-2">/ 100</span>
                    </div>
                    <div
                      data-testid="aesthetics-category"
                      className="text-sm text-gray-300 mt-2"
                    >
                      {result.category}
                    </div>
                  </div>

                  <div className="md:text-right">
                    <div className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">
                      Aktuális arányod
                    </div>
                    <div
                      data-testid="aesthetics-ratio"
                      className="text-4xl md:text-5xl font-bold text-white"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                      {result.ratio.toFixed(3)}
                    </div>
                    <div className="text-sm text-yellow-400 mt-1">
                      Ideális: {result.ideal_ratio.toFixed(3)}
                    </div>
                  </div>
                </div>

                {/* Visual ratio bar */}
                <div className="mt-6 pt-6 border-t border-gray-700">
                  <div className="text-xs text-gray-400 mb-2 flex items-center justify-between">
                    <span>0</span>
                    <span className="text-yellow-400 font-semibold">
                      Ideális ({result.ideal_ratio.toFixed(3)})
                    </span>
                    <span>{(result.ideal_ratio * 1.5).toFixed(2)}</span>
                  </div>
                  <div className="relative h-3 bg-gray-700 rounded-full overflow-hidden">
                    {/* Ideal marker */}
                    <div
                      className="absolute top-0 bottom-0 w-0.5 bg-yellow-400 z-10"
                      style={{ left: `${idealBarPct}%` }}
                    ></div>
                    {/* Your ratio fill */}
                    <div
                      data-testid="aesthetics-ratio-bar"
                      className="absolute top-0 bottom-0 left-0 bg-gradient-to-r from-yellow-600 to-yellow-400 rounded-full transition-all duration-700 ease-out"
                      style={{ width: `${ratioBarPct}%` }}
                    ></div>
                  </div>
                </div>
              </div>

              {/* Secondary stats grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 pb-8 border-b border-gray-200">
                <div
                  data-testid="aesthetics-stat-deviation"
                  className="bg-white border-2 border-gray-100 hover:border-yellow-600 p-5 rounded-xl transition-all"
                >
                  <div className="flex items-center gap-2 text-yellow-600 text-xs font-bold tracking-widest uppercase mb-2">
                    <Target size={14} /> Eltérés
                  </div>
                  <div className="text-2xl font-bold text-gray-900">
                    {result.deviation_pct.toFixed(1)}
                    <span className="text-lg text-gray-500 ml-1">%</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">az ideális aránytól</div>
                </div>

                <div
                  data-testid="aesthetics-stat-cm-gap"
                  className="bg-white border-2 border-gray-100 hover:border-yellow-600 p-5 rounded-xl transition-all"
                >
                  <div className="flex items-center gap-2 text-yellow-600 text-xs font-bold tracking-widest uppercase mb-2">
                    <Ruler size={14} /> Cm a célig
                  </div>
                  <div className="text-2xl font-bold text-gray-900">
                    {result.cm_to_ideal > 0 ? '+' : ''}
                    {result.cm_to_ideal.toFixed(1)}
                    <span className="text-lg text-gray-500 ml-1">cm</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    a {cmFieldSuffix[result.cm_to_ideal_field] || result.cm_to_ideal_field}
                  </div>
                </div>

                <div
                  data-testid="aesthetics-stat-index"
                  className="bg-yellow-50 border-2 border-yellow-600 p-5 rounded-xl"
                >
                  <div className="flex items-center gap-2 text-yellow-700 text-xs font-bold tracking-widest uppercase mb-2">
                    <TrendingUp size={14} /> Index típus
                  </div>
                  <div className="text-xl font-bold text-gray-900">
                    {result.index_type === 'adonis' ? 'Adonis Index' : 'Venus Index'}
                  </div>
                  <div className="text-xs text-gray-700 mt-1">
                    {result.index_type === 'adonis'
                      ? 'Váll / Derék arány'
                      : 'Derék / Csípő arány'}
                  </div>
                </div>
              </div>

              {/* === Phase 2 Trigger Button === */}
              {!macroOpen && (
                <div className="mb-8 text-center">
                  <Button
                    type="button"
                    data-testid="aesthetics-open-macro-btn"
                    onClick={handleOpenMacroPhase}
                    className="group w-full sm:w-auto sm:inline-flex flex items-center justify-center gap-2 sm:gap-3 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-gray-900 font-bold px-4 sm:px-7 py-4 sm:py-6 text-[13px] sm:text-base leading-snug rounded-xl shadow-xl shadow-yellow-500/30 transition-all duration-300 whitespace-normal h-auto text-center"
                  >
                    <Flame size={18} className="shrink-0" />
                    <span className="break-words">
                      <span className="sm:hidden">Személyre szabott makrók</span>
                      <span className="hidden sm:inline">Tovább a személyre szabott makrotápanyagokhoz</span>
                    </span>
                    <ArrowRight size={16} className="shrink-0 transition-transform duration-300 group-hover:translate-x-1.5" />
                  </Button>
                  <p className="text-xs text-gray-500 mt-3 px-2">
                    Másodperceken belül megkapod a napi kalória- és makrótápanyag-célodat.
                  </p>
                </div>
              )}

              {/* === Phase 2: Macronutrient Calculator (extracted) === */}
              <MacroPhase
                open={macroOpen}
                baseProfile={{
                  gender:     form.gender,
                  age_years:  form.age_years,
                  height_cm:  form.height_cm,
                  weight_kg:  form.weight_kg,
                }}
              />

              {/* Save Results via Email */}
              <div
                data-testid="aesthetics-save-block"
                className="mb-8 bg-gradient-to-br from-gray-50 to-yellow-50/40 border border-gray-200 rounded-xl p-6 md:p-8"
              >
                {isSaved ? (
                  <div className="text-center py-4" data-testid="aesthetics-save-success">
                    <CheckCircle2 className="mx-auto text-green-500 mb-3" size={48} />
                    <h4
                      className="text-xl font-bold text-gray-900 mb-2"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                      Eredmények elmentve!
                    </h4>
                    <p className="text-gray-700 text-sm">
                      Hamarosan elküldjük a részletes esztétikai elemzést a{' '}
                      <span className="font-semibold">{saveEmail}</span> címre.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start gap-3 mb-4">
                      <div className="bg-yellow-600/10 border border-yellow-600/20 p-2 rounded-lg flex-shrink-0">
                        <Mail className="text-yellow-600" size={20} />
                      </div>
                      <div>
                        <h4
                          className="text-lg md:text-xl font-bold text-gray-900 leading-tight mb-1"
                          style={{ fontFamily: 'Montserrat, sans-serif' }}
                        >
                          Mentsd el az esztétikai elemzésed
                        </h4>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          Add meg az e-mail címed, és elküldjük a részletes
                          értelmezést és az ajánlott fókuszpontokat a postafiókodba.
                        </p>
                      </div>
                    </div>

                    <form
                      onSubmit={handleSaveResults}
                      className="flex flex-col sm:flex-row gap-3"
                      data-testid="aesthetics-save-form"
                    >
                      <Input
                        data-testid="aesthetics-save-email-input"
                        type="email"
                        required
                        value={saveEmail}
                        onChange={(e) => setSaveEmail(e.target.value)}
                        placeholder="Add meg az e-mail címed..."
                        className="flex-1 h-12 text-base"
                      />
                      <Button
                        data-testid="aesthetics-save-submit-btn"
                        type="submit"
                        disabled={isSaving}
                        className="bg-gray-900 hover:bg-yellow-600 text-white font-bold h-12 px-6 transition-all duration-300 whitespace-nowrap"
                      >
                        {isSaving ? 'Mentés…' : 'Eredmények mentése'}
                        <Mail className="ml-2" size={18} />
                      </Button>
                    </form>

                    <p className="text-xs text-gray-500 mt-3">
                      Az „Eredmények mentése&rdquo; gombra kattintva elfogadod az{' '}
                      <a
                        href="/adatkezelesi-tajekoztato"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline text-yellow-700 hover:text-yellow-600 font-semibold"
                      >
                        adatkezelési tájékoztatót
                      </a>
                      , és hozzájárulsz, hogy az Orosz Coaching automatizált
                      e-maileket küldjön az eredményekről. Bármikor leiratkozhatsz egy
                      kattintással.
                    </p>
                  </>
                )}
              </div>

              {/* CTA Banner */}
              <div
                data-testid="aesthetics-cta-banner"
                className="bg-gradient-to-r from-yellow-600 to-yellow-700 text-white rounded-xl p-6 md:p-8"
              >
                <div className="md:flex md:items-center md:justify-between gap-6">
                  <div className="mb-4 md:mb-0">
                    <h4
                      className="text-xl md:text-2xl font-bold mb-2 leading-tight"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                      Szeretnél eljutni a klasszikus Aranyarányhoz?
                    </h4>
                    <p className="text-yellow-50">
                      Egy célzott edzésterv 6–12 hónap alatt látható változást hoz.
                      Foglalj ingyenes konzultációt, és építsünk együtt!
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 md:flex-shrink-0">
                    <button
                      type="button"
                      data-testid="aesthetics-cta-book"
                      onClick={onBookClick}
                      className="inline-flex items-center justify-center gap-2 bg-white text-gray-900 font-bold px-6 py-3 rounded-lg hover:bg-gray-100 transition-all whitespace-nowrap"
                    >
                      Konzultáció foglalása <ArrowRight size={16} />
                    </button>
                    <a
                      href="#programs"
                      data-testid="aesthetics-cta-programs"
                      className="inline-flex items-center justify-center gap-2 border-2 border-white text-white font-bold px-6 py-3 rounded-lg hover:bg-white hover:text-gray-900 transition-all whitespace-nowrap"
                    >
                      Programok
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default AestheticsCalculatorSection;
